import React, {useEffect, useRef} from "react";
import {default as LibraryPhoneInput} from "react-phone-input-2";
import clsx from "clsx";
import {buildStyles} from "../../../buildStyles";
import InputBase from "../InputBase";
import {useTheme} from "../../../../theme/ThemeProvider";
import FlexContainer from "../../../layout/FlexContainer";

const useStyles = (theme) =>
  buildStyles({
    input: {
      width: "100% !important",
      height: "24px",
      padding: theme.spacing(0, 0, 0, 11) + "!important",
      fontWeight: `${theme.typography.textSM.regular.fontWeight} !important`,
      fontSize: `${theme.typography.textSM.regular.fontSize} !important`,
      fontFamily: `${theme.typography.textSM.regular.fontFamily} !important`,
      color: `${theme.colors.textPrimary} !important`,
      border: "none !important",
      boxShadow: "none !important",
      backgroundColor: "transparent !important",
    },
    button: {
      height: "24px",
      "& .selected-flag": {padding: 0, width: "fit-content"},
      "&.-disabled": {display: "none"},
    },
    noFlag: {"& .selected-flag div.flag": {backgroundAttachment: "fixed"}},
    dropdown: {
      position: "fixed !important",
      width: "210px !important",
      borderRadius: "10px !important",
      boxShadow: `${theme.shadows.md} !important`,
      "&::-webkit-scrollbar": {width: "0px !important"},
      "& li.country": {padding: `${theme.spacing(2.5, 4, 2, 12)} !important`},
      "& li.country .flag": {margin: 0},
      "& li.country .dial-code": {color: theme.colors.textTertiary},
    },
    search: {
      width: "100%",
      padding: `${theme.spacing(3, 3, 1)} !important`,
      "& input": {
        border: `1px solid ${theme.colors.border} !important`,
        borderRadius: `${theme.spacing(2)} !important`,
        marginLeft: "0 !important",
        width: "100%",
      },
    },
  });

export default function PhoneInput({
  inputBase: InputBaseComponent = InputBase,
  phone,
  edit,
  setCountry = null,
  allowEmpty = false,
  onChange = () => null,
  onKeyDown = () => null,
  setValidPhone = () => null,
  onFocus,
  onBlur,
  error = false,
  sx = {},
}) {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const phoneRef = useRef(null);

  useEffect(() => {
    if (phoneRef.current && phone) {
      const timer = setTimeout(() => {
        const {selectedCountry} = phoneRef.current.state || {};
        if (selectedCountry) {
          setCountry &&
            setCountry({
              name: selectedCountry.name,
              dialCode: selectedCountry.dialCode,
              countryCode: selectedCountry.iso2,
            });
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [phone, setCountry]);

  const validate = (newPhone, country) => {
    if (!!allowEmpty && !newPhone?.length) {
      return true;
    } else {
      return (
        newPhone.length > 1 &&
        country.hasOwnProperty("dialCode") &&
        newPhone.substring(country.dialCode.length).length >= 5
      );
    }
  };

  const handlePhoneChange = (newPhone, newCountry) => {
    if (!!setCountry) {
      setCountry(newCountry);
    }
    onChange(newPhone);
    const isPhoneValid = validate(newPhone, newCountry);
    setValidPhone(isPhoneValid);
  };

  return (
    <InputBaseComponent
      inputProps={null}
      actions={
        <FlexContainer alignItems={"center"} width="100%">
          <LibraryPhoneInput
            ref={phoneRef}
            value={phone}
            enableSearch
            specialLabel=""
            disableSearchIcon
            enableLongNumbers
            priority={{ca: 0, us: 1}}
            onChange={handlePhoneChange}
            disabled={!edit}
            preferredCountries={["ca", "us", "fr"]}
            inputClass={classes.input}
            buttonClass={clsx(classes.button, {
              [classes.noFlag]: !phone,
            })}
            searchClass={classes.search}
            dropdownClass={classes.dropdown}
            inputProps={{
              autoFocus: true,
              onKeyDown: onKeyDown,
              onFocus: onFocus,
              onBlur: onBlur,
            }}
          />
        </FlexContainer>
      }
      sx={{
        border: error ? "1px solid red" : "1px solid " + theme.colors.gray300,
        ...sx,
      }}
    />
  );
}
